'use client';

import { useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import type { CommonProps } from '@mui/material/OverridableComponent';
import type { FadedEdgesBoxProps } from './types';
type HorizontalProps<T> = FadedEdgesBoxProps<T>;
export function Horizontal<T extends CommonProps & {
  children?: React.ReactNode;
  ref?: React.Ref<unknown>;
  sx?: SxProps<Theme>;
}>(props: HorizontalProps<T>) {
  const {
    Component,
    fadeColor = '#fff',
    fadeSize = '12%',
    offsetStart = 10,
    offsetEnd = 10,
    overrideRef,
    showStartFadeByDefault = false,
    showEndFadeByDefault = true,
    ...rest
  } = props;
  const {
    sx,
    children
  } = rest;
  const boxRef = useRef<HTMLDivElement>(null);
  const [showFades, setShowFades] = useState<{
    showStartingFade: boolean;
    showEndingFade: boolean;
  }>({
    showStartingFade: showStartFadeByDefault,
    showEndingFade: showEndFadeByDefault
  });
  useEffect(() => {
    const refToUse = overrideRef ? overrideRef.current : boxRef.current;
    const scrollHandler = () => {
      if (refToUse) {
        const {
          clientWidth,
          scrollWidth,
          scrollLeft
        } = refToUse;
        setShowFades(prevState => ({
          ...prevState,
          showStartingFade: scrollLeft - offsetStart > 0,
          showEndingFade: !(scrollLeft + offsetEnd > scrollWidth - clientWidth)
        }));
      }
    };
    if (refToUse) {
      scrollHandler();
    }
    if (refToUse) {
      scrollHandler();
    }
    refToUse?.addEventListener('scroll', scrollHandler, {
      passive: true
    });
    return () => {
      refToUse?.removeEventListener('scroll', scrollHandler);
    };
  }, [offsetStart, offsetEnd, overrideRef?.current]);
  const {
    showStartingFade,
    showEndingFade
  } = showFades;
  const containerCommonValues = {
    position: 'absolute',
    content: '""',
    pointerEvents: 'none',
    zIndex: 1,
    bottom: 0,
    top: 0,
    transition: 'width 0.2s',
    willChange: 'width'
  };
  return <Component ref={overrideRef ?? boxRef} {...rest} sx={{
    '&:before': {
      ...containerCommonValues,
      background: `linear-gradient(${270}deg,hsla(0,0%,100%,0) 0,${fadeColor})`,
      left: 0,
      width: showStartingFade ? fadeSize : 0
    },
    '&:after': {
      ...containerCommonValues,
      background: `linear-gradient(${90}deg,hsla(0,0%,100%,0) 0,${fadeColor})`,
      right: 0,
      width: showEndingFade ? fadeSize : 0
    },
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    overflowX: 'auto',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    ...sx
  }}>
      {children}
    </Component>;
}